import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/Icon'
import { createWorkItem } from '@/features/work-items/api'
import type { ApiError } from '@/lib/api/client'
import type { WorkItem } from '@/types/work'

/**
 * Adding a task where the tasks are.
 *
 * A collapsed line rather than a button that opens a form: the overwhelming
 * majority of new tasks are a title and nothing else, and every field you make
 * someone fill in first is a task that gets written on paper instead. Everything
 * else about it is set afterwards in the panel, where it can be seen.
 *
 * It stays open after a create, because tasks arrive in handfuls — you are
 * writing down what was just agreed in a meeting, not filing one thing.
 *
 * `stateId` is the column being added to. Omitted, the server drops the task in
 * the first state of the project's workflow, which is what an empty project
 * needs.
 */
export function NewTask({
  projectId,
  stateId,
  onCreated,
}: {
  projectId: string
  stateId?: string | undefined
  onCreated?: ((item: WorkItem) => void) | undefined
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')

  const create = useMutation<WorkItem, ApiError, string>({
    mutationFn: (name) =>
      createWorkItem(stateId === undefined ? { projectId, title: name } : { projectId, title: name, stateId }),
    onSuccess: (item) => {
      setTitle('')
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      void queryClient.invalidateQueries({ queryKey: ['board'] })
      void queryClient.invalidateQueries({ queryKey: ['timeline'] })
      void queryClient.invalidateQueries({ queryKey: ['activities'] })
      onCreated?.(item)
    },
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 flex w-full items-center gap-1.5 rounded px-3 py-2 text-[13px]"
      >
        <Icon name="plus" className="size-3.5" />
        {t('work.addTask')}
      </button>
    )
  }

  return (
    <form
      className="flex items-center gap-2 px-3 py-2"
      onSubmit={(event) => {
        event.preventDefault()
        const name = title.trim()
        if (name.length > 0) create.mutate(name)
      }}
    >
      <Icon name="plus" className="text-ink-500 size-3.5 shrink-0" />

      <input
        value={title}
        autoFocus
        disabled={create.isPending}
        aria-label={t('work.addTask')}
        placeholder={t('work.newTaskTitle')}
        onChange={(event) => setTitle(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setTitle('')
            setOpen(false)
          }
        }}
        // Closing on blur would eat a half-typed title every time someone
        // glances away, so it closes on Escape or on the button, never by
        // losing focus.
        className="border-line bg-surface min-w-0 flex-1 rounded border px-2 py-1 text-[13px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
      />

      <button
        type="button"
        onClick={() => {
          setTitle('')
          setOpen(false)
        }}
        title={t('view.close')}
        aria-label={t('view.close')}
        className="text-ink-500 hover:text-ink-900 shrink-0 rounded p-1"
      >
        <Icon name="close" className="size-3.5" />
      </button>

      {create.isError ? (
        <span className="shrink-0 text-[11px] text-[color:var(--color-state-blocked)]">
          {create.error.message}
        </span>
      ) : null}
    </form>
  )
}
